// The `SecondaryBanner` component is a React functional component that displays a list of banners,
// each containing an image and a call-to-action (CTA) button. It takes `content` as a prop,
// which includes an array of images and CTA buttons. Each image adjusts dynamically based on the
// device viewport using `<picture>` for responsive loading. The CTA buttons are styled individually based on
// the data provided, allowing for customizable colors, text, and hover effects. This component provides a
// flexible layout for showcasing banners with interactive elements.

import React from 'react';
import { SecondaryBannerDataSource } from 'types/next';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import useLocale from 'helpers/hooks/useLocale';

type SecondaryBannerData = {
  content: SecondaryBannerDataSource[];
};

const SecondaryBanner = ({ content }: SecondaryBannerData) => {
  const { getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';

  return (
    <div className="flex flex-col space-y-4">
      {content &&
        content.length > 0 &&
        content.map((image: SecondaryBannerDataSource, index: number) => {
          const mobileImage = image?.imageUrlMobile?.[selectedLocale];
          const desktopImage = image?.imageUrlDesktop?.[selectedLocale];
          const cta = image?.ctaButton;

          const buttonText = cta?.buttonText?.[selectedLocale] || '';
          const buttonLink = cta?.buttonLink?.[selectedLocale] || '';

          return (
            <div className="relative w-full" key={index}>
              <SmartLink href={buttonLink}>
                <picture>
                  {mobileImage && (
                    <source
                      srcSet={`https://${mobileImage.defaultHost}/i/${mobileImage.endpoint}/${mobileImage.name}?fmt=webp`}
                      media="(max-width: 768px)"
                    />
                  )}
                  {desktopImage && (
                    <img
                      src={`https://${desktopImage.defaultHost}/i/${desktopImage.endpoint}/${desktopImage.name}?fmt=webp`}
                      alt={`Banner image ${index + 1}`}
                      className="w-full object-cover p-16"
                      loading="lazy"
                    />
                  )}
                </picture>
                <button
                  style={{
                    backgroundColor: cta?.buttonColour,
                    color: cta?.buttonTextColour,
                  }}
                  className="absolute bottom-[39px] right-[35px] h-[30px] min-w-[123px] rounded px-24 py-8 font-body text-14 font-bold transition-all duration-200"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = cta?.buttonHoverColour;
                    e.currentTarget.style.color = cta?.buttonHoverText;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = cta?.buttonColour;
                    e.currentTarget.style.color = cta?.buttonTextColour;
                  }}
                >
                  {buttonText}
                </button>
              </SmartLink>
            </div>
          );
        })}
    </div>
  );
};

export default SecondaryBanner;
